import {
  getUserStorageKey,
  safeLocalStorageParse,
  safeLocalStorageRead,
  safeLocalStorageRemove,
  safeLocalStorageWrite,
} from './localStorage';

export interface ScopedCacheConfig {
  namespace: string;
  schemaVersion: string;
  ttlMs: number;
  maxEntries: number;
}

interface CacheEntry<TValue, TKey> {
  value: TValue;
  cachedAt: number;
  invalidationKey: TKey;
}

export interface CacheLifecycle {
  sweep(userId: string): void;
  upgrade(userId: string): void;
}

export interface ScopedCache<TValue, TKey> extends CacheLifecycle {
  read(userId: string, entryId: string, currentKey: TKey | null): TValue | null;
  write(userId: string, entryId: string, value: TValue, key: TKey): void;
  touch(userId: string, entryId: string): void;
  remove(userId: string, entryId: string): void;
}

export const createScopedCache = <TValue, TKey>(
  config: ScopedCacheConfig
): ScopedCache<TValue, TKey> => {
  const { namespace, schemaVersion, ttlMs, maxEntries } = config;

  const buildNamespacePrefix = (userId: string) => `${getUserStorageKey(userId)}_${namespace}_`;

  const buildVersionedPrefix = (userId: string, version: string) =>
    `${buildNamespacePrefix(userId)}${version}_`;

  const buildEntryKey = (userId: string, entryId: string) =>
    `${buildVersionedPrefix(userId, schemaVersion)}${entryId}`;

  const buildIndexKey = (userId: string) => `${buildVersionedPrefix(userId, schemaVersion)}index`;

  const readIndex = (userId: string): string[] =>
    safeLocalStorageParse<string[]>(safeLocalStorageRead(buildIndexKey(userId))) ?? [];

  const writeIndex = (userId: string, ids: string[]): void => {
    safeLocalStorageWrite(buildIndexKey(userId), JSON.stringify(ids));
  };

  const moveToFront = (ids: string[], entryId: string): string[] => [
    entryId,
    ...ids.filter((id) => id !== entryId),
  ];

  const isExpired = (entry: CacheEntry<TValue, TKey>): boolean =>
    Date.now() - entry.cachedAt > ttlMs;

  const removeEntry = (userId: string, entryId: string): void => {
    if (!userId) {
      return;
    }
    safeLocalStorageRemove(buildEntryKey(userId, entryId));
    const ids = readIndex(userId);
    const filtered = ids.filter((id) => id !== entryId);
    if (filtered.length !== ids.length) {
      writeIndex(userId, filtered);
    }
  };

  const read = (userId: string, entryId: string, currentKey: TKey | null): TValue | null => {
    if (!userId) {
      return null;
    }
    const entry = safeLocalStorageParse<CacheEntry<TValue, TKey>>(
      safeLocalStorageRead(buildEntryKey(userId, entryId))
    );
    if (!entry) {
      return null;
    }
    if (isExpired(entry)) {
      removeEntry(userId, entryId);
      return null;
    }
    if (currentKey !== null && entry.invalidationKey !== currentKey) {
      removeEntry(userId, entryId);
      return null;
    }
    return entry.value;
  };

  const write = (userId: string, entryId: string, value: TValue, key: TKey): void => {
    if (!userId) {
      return;
    }
    const entry: CacheEntry<TValue, TKey> = { value, cachedAt: Date.now(), invalidationKey: key };
    safeLocalStorageWrite(buildEntryKey(userId, entryId), JSON.stringify(entry));

    const ids = moveToFront(readIndex(userId), entryId);

    if (ids.length > maxEntries) {
      const evicted = ids.pop();
      if (evicted !== undefined) {
        safeLocalStorageRemove(buildEntryKey(userId, evicted));
      }
    }

    writeIndex(userId, ids);
  };

  const touch = (userId: string, entryId: string): void => {
    if (!userId) {
      return;
    }
    const ids = readIndex(userId);
    if (ids[0] === entryId) {
      return;
    }
    if (!ids.includes(entryId)) {
      return;
    }
    writeIndex(userId, moveToFront(ids, entryId));
  };

  const sweep = (userId: string): void => {
    if (!userId) {
      return;
    }
    const ids = readIndex(userId);
    const surviving: string[] = [];
    for (const id of ids) {
      const entry = safeLocalStorageParse<CacheEntry<TValue, TKey>>(
        safeLocalStorageRead(buildEntryKey(userId, id))
      );
      if (!entry || isExpired(entry)) {
        safeLocalStorageRemove(buildEntryKey(userId, id));
      } else {
        surviving.push(id);
      }
    }
    if (surviving.length !== ids.length) {
      writeIndex(userId, surviving);
    }
  };

  const upgrade = (userId: string): void => {
    if (!userId) {
      return;
    }
    const namespacePrefix = buildNamespacePrefix(userId);
    const currentPrefix = buildVersionedPrefix(userId, schemaVersion);

    const orphans: string[] = [];
    try {
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith(namespacePrefix) && !key.startsWith(currentPrefix)) {
          orphans.push(key);
        }
      }
    } catch (error) {
      console.warn('scopedCache: upgrade scan failed', error);
      return;
    }

    if (orphans.length === 0) {
      return;
    }

    for (const key of orphans) {
      safeLocalStorageRemove(key);
    }
  };

  return { read, write, touch, remove: removeEntry, sweep, upgrade };
};
